import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { inviteService, getErrorMessage } from '../../../services/apiService';
import { natureLabel, oemOdmLabel, subcategoryLabel, approverNames } from '../../../utils/classification';
import InviteReviewDialog from './InviteReviewDialog';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const ago = (d) => { const n = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)); return n === 0 ? 'Today' : n === 1 ? '1 day ago' : `${n} days ago`; };

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={18} className="text-slate-700" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
    </div>
  </div>
);

/**
 * Approvals › Invite Approval: invites waiting for their routed approvers. Administrators see every
 * pending invite; an approver sees the ones routed to them; a coordinator also sees the invites they
 * raised, so they can follow them. Each card looks like a Level 1 card and has one action, View
 * Details: the popup (InviteReviewDialog) shows the invite and, for those who may decide, Approve and
 * send link / Reject.
 */
const InviteApprovalsPanel = ({ onCountChange, onDecided, refreshKey = 0 }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await inviteService.getApprovals({ status: 'PENDING_APPROVAL', limit: 100 });
      const rows = res?.data || [];
      setInvites(rows);
      onCountChange?.(res?.meta?.total ?? rows.length);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const decided = async () => {
    setSelected(null);
    await load();
    onDecided?.();
  };

  if (loading && invites.length === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white"><CardContent className="p-10 text-center text-slate-600">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-3" />Loading invites…
      </CardContent></Card>
    );
  }
  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50"><CardContent className="p-4 flex items-center gap-3">
        <Icon name="AlertCircle" size={20} className="text-red-600" /><p className="text-red-800 flex-1">{error}</p>
        <Button variant="outline" size="sm" onClick={load}>Retry</Button>
      </CardContent></Card>
    );
  }
  if (invites.length === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white"><CardContent className="p-6">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="MailCheck" size={40} className="text-slate-400" /></div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No invites awaiting approval</h3>
          <p className="text-sm text-slate-600 max-w-md mx-auto">New invites appear here for the approvers named in User Management › Invite approvers, and for whoever raised them.</p>
        </div>
      </CardContent></Card>
    );
  }

  return (
    <>
      <div className="space-y-4" data-testid="invite-approvals">
        {invites.map((invite) => (
          <Card key={invite.id} className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-300" data-testid={`invite-approval-${invite.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 truncate mb-2">{invite.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                    <span className="flex items-center gap-1.5"><Icon name="Calendar" size={14} /><span>Requested {formatDate(invite.created_at)}</span></span>
                    <span className="flex items-center gap-1.5"><Icon name="Clock" size={14} /><span>{ago(invite.created_at)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  {invite.raised_by_me && (
                    <span className="inline-flex items-center px-2 py-1 bg-sky-100 text-sky-800 text-xs font-medium rounded-full">RAISED BY YOU</span>
                  )}
                  <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                    <Icon name="Clock" size={12} className="mr-1" />AWAITING APPROVAL
                  </span>
                </div>
              </div>

              <div className="h-px bg-slate-200 my-4" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <InfoItem icon="Factory" label="Nature of business" value={natureLabel(invite.nature_of_business)} />
                <InfoItem icon="Tag" label="Supplier type" value={oemOdmLabel(invite.oem_odm)} />
                <InfoItem icon="Layers" label="Sub-category" value={subcategoryLabel(invite.supplier_subcategory)} />
                <InfoItem
                  icon="UserCheck"
                  label={(invite.approvers || []).length > 1 ? 'Approvers (any one)' : 'Approver'}
                  value={approverNames(invite.approvers, 'Any administrator')}
                />
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-slate-600 flex-wrap">
                <span className="flex items-center gap-1.5"><Icon name="Mail" size={14} /><span className="truncate">{invite.email}</span></span>
                <span className="flex items-center gap-1.5">
                  <Icon name="User" size={14} />
                  <span className="truncate">Requested by {invite.raised_by_me ? 'you' : invite.created_by_user?.full_name || invite.created_by_user?.email || 'a colleague'}</span>
                </span>
                {invite.supplier_request_id ? (
                  <span className="flex items-center gap-1.5"><Icon name="FileText" size={14} /><span>Supplier request #{invite.supplier_request_id}</span></span>
                ) : null}
              </div>

              <div className="h-px bg-slate-200 my-4" />

              <div className="flex justify-end">
                <Button variant="default" onClick={() => setSelected(invite)} iconName="ArrowRight" iconPosition="right">View Details</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {selected && <InviteReviewDialog invite={selected} onClose={() => setSelected(null)} onDecided={decided} />}
    </>
  );
};

export default InviteApprovalsPanel;
